import { useCallback, useEffect, useState } from 'react'
import { collectionsApi, type CollectionHealth } from '../services/collectionsApi'

/**
 * A collection's accumulation signals, fetched once on mount so the counts are
 * on screen before anyone asks for them.
 *
 * That "before anyone asks" is the whole point. Prune and Merge already exist,
 * but both are silent until entered — you had to guess there was something to
 * find. Nobody guesses, which is how one account reached 443 unreferenced
 * People stubs, more than half the collection, with nothing on the page
 * different from a healthy one.
 *
 * `refresh` is for after a prune or a merge: the numbers the user just changed
 * are the ones they will look at next.
 *
 * A failed fetch leaves `health` null and is NOT surfaced. These are hints on
 * someone else's page; an error banner for a badge that could not load costs
 * more attention than the badge is worth.
 */
export function useCollectionHealth(collectionId: number | null | undefined) {
    const [health, setHealth] = useState<CollectionHealth | null>(null)

    const refresh = useCallback(async () => {
        if (collectionId == null) {
            setHealth(null)
            return
        }
        try {
            setHealth(await collectionsApi.items.health(collectionId))
        } catch (err) {
            console.error('Failed to load collection health:', err)
        }
    }, [collectionId])

    useEffect(() => {
        void refresh()
    }, [refresh])

    return { health, refresh }
}
